import React, { forwardRef } from "react";
import { money, personBreakdown } from "@/lib/format";
import { describePayment } from "@/lib/payments";
import { LOGO_DATA_URI } from "@/lib/logoData";

// Branded, receipt-styled card used purely as a capture target for "Save as
// image". Rendered off-screen; uses inline styles so the export looks the same
// regardless of light/dark theme.
const ink = "#0F172A";
const muted = "#64748B";
const line = "#CBD5E1";

const Row = ({ l, r, strong, big }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "3px 0" }}>
    <span style={{ color: strong ? ink : muted, fontWeight: strong ? 700 : 400, fontSize: big ? 18 : 13 }}>{l}</span>
    <span style={{ color: ink, fontWeight: strong ? 700 : 500, fontSize: big ? 18 : 13, whiteSpace: "nowrap" }}>{r}</span>
  </div>
);

const dashed = { borderTop: `2px dashed ${line}`, margin: "10px 0" };

export const ReceiptCard = forwardRef(({ title, summary, participants, items, payment }, ref) => {
  if (!summary) return null;
  const people = summary.people || [];
  const cur = summary.currency || "IDR";
  const dec = summary.decimals ?? 0;
  const m = (v) => money(v, cur);
  const now = new Date().toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div
      ref={ref}
      style={{
        width: 380,
        background: "#ffffff",
        color: ink,
        fontFamily: "Jost, ui-sans-serif, system-ui, sans-serif",
        padding: "26px 24px 30px",
        boxSizing: "border-box",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <img
          src={LOGO_DATA_URI}
          alt="gigazip splitbill"
          style={{ width: 190, height: "auto", display: "block", margin: "0 auto" }}
        />
        <div style={{ fontSize: 11, color: muted, letterSpacing: 3, marginTop: 8 }}>BILL RECEIPT</div>
      </div>

      <div style={dashed} />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: muted }}>
        <span style={{ fontWeight: 600, color: ink }}>{title || "Bill"}</span>
        <span>{now}</span>
      </div>
      <div style={dashed} />

      <Row l="Subtotal" r={m(summary.subtotal)} />
      {summary.discount > 0 && <Row l="Discount" r={`- ${m(summary.discount)}`} />}
      {summary.service_amount > 0 && <Row l="Service" r={m(summary.service_amount)} />}
      {summary.tax_amount > 0 && <Row l="Tax" r={m(summary.tax_amount)} />}
      {summary.rounding !== 0 && <Row l="Rounding" r={`${summary.rounding < 0 ? "- " : "+ "}${m(Math.abs(summary.rounding))}`} />}
      <div style={{ borderTop: `2px solid ${ink}`, margin: "8px 0" }} />
      <Row l="TOTAL" r={m(summary.grand_total)} strong big />

      <div style={dashed} />
      <div style={{ fontSize: 11, color: muted, letterSpacing: 2, marginBottom: 4 }}>PER PERSON</div>

      {people.map((p) => {
        const bd = personBreakdown(p, items || [], dec);
        return (
          <div key={p.id} style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <span style={{ width: 10, height: 10, borderRadius: 999, background: p.color || muted, display: "inline-block", flex: "0 0 auto" }} />
                <span style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</span>
              </span>
              <span style={{ fontWeight: 700, fontSize: 14, whiteSpace: "nowrap" }}>{m(p.total)}</span>
            </div>
            {bd.lines.map((l) => (
              <div key={l.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 11, color: muted, paddingLeft: 18 }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</span>
                <span style={{ whiteSpace: "nowrap" }}>{m(l.amount)}</span>
              </div>
            ))}
            {(bd.service > 0 || bd.tax > 0 || bd.discount > 0 || bd.rounding !== 0) && (
              <div style={{ fontSize: 10, color: muted, paddingLeft: 18, marginTop: 1 }}>
                {[
                  bd.discount > 0 ? `disc -${m(bd.discount)}` : null,
                  bd.service > 0 ? `service ${m(bd.service)}` : null,
                  bd.tax > 0 ? `tax ${m(bd.tax)}` : null,
                  bd.rounding !== 0 ? `rounding ${bd.rounding < 0 ? "-" : "+"}${m(Math.abs(bd.rounding))}` : null,
                ].filter(Boolean).join("  ·  ")}
              </div>
            )}
          </div>
        );
      })}

      {summary.has_unassigned && (
        <>
          <div style={dashed} />
          <Row l="Unassigned items" r={m(summary.unassigned_amount)} />
        </>
      )}

      {(() => {
        const pd = describePayment(payment);
        if (!pd) return null;
        const extra = pd.rows.filter((r) => r.value !== pd.copyValue).map((r) => (r.key === "bsb" ? `${r.label} ${r.value}` : r.value));
        return (
          <>
            <div style={dashed} />
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 10, color: muted, letterSpacing: 2, marginBottom: 3 }}>EVERYONE PAYS TO · {pd.currency}</div>
              <div style={{ fontSize: 12, color: muted, marginBottom: 1 }}>{pd.headline}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: ink }}>{[...extra, pd.copyValue].filter(Boolean).join("  ·  ")}</div>
              {pd.name && <div style={{ fontSize: 12, color: muted, marginTop: 1 }}>a/n {pd.name}</div>}
            </div>
          </>
        );
      })()}

      <div style={dashed} />
      <div style={{ textAlign: "center", fontSize: 11, color: muted }}>
        Split fairly with <span style={{ color: ink, fontWeight: 600 }}>gigazip</span> · gigazipsplitbill.com
      </div>
    </div>
  );
});

ReceiptCard.displayName = "ReceiptCard";
